import React, { useState, useRef } from 'react';
import { Phone, Mail, MapPin, Clock, ShieldAlert, Send, CheckCircle2, AlertTriangle, Upload, X } from 'lucide-react';
import { companyInfo } from '../data';
import useMeta from '../hooks/useMeta';

export default function Contact() {
  useMeta(
    'Kontakt LJR Teknik ApS | Få et Uforpligtende Tilbud',
    'Brug for akut service, reparation eller et uforpligtende tilbud på din gaffeltruck eller maskine? Kontakt LJR Teknik ApS på tlf. 88 44 38 38.'
  );

  const formRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  // File constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 20;
  const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    // Check total file count
    if (files.length + selectedFiles.length > MAX_FILES) {
      setStatus({
        type: 'error',
        message: `Du kan højst uploade ${MAX_FILES} filer i alt.`
      });
      return;
    }

    const validFiles = [];
    let totalSize = files.reduce((sum, f) => sum + f.size, 0);

    for (const file of selectedFiles) {
      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        setStatus({
          type: 'error',
          message: `Filen "${file.name}" har et ikke-understøttet format. Kun PDF, PNG, JPG/JPEG, GIF og WEBP er tilladt.`
        });
        return;
      }

      // Validate individual file size
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setStatus({
          type: 'error',
          message: `Filen "${file.name}" er for stor. Maksimal størrelse pr. fil er ${MAX_FILE_SIZE_MB} MB.`
        });
        return;
      }

      totalSize += file.size;
      validFiles.push(file);
    }

    // Validate total size
    if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
      setStatus({
        type: 'error',
        message: `Den samlede størrelse af alle filer må ikke overstige ${MAX_TOTAL_SIZE_MB} MB.`
      });
      return;
    }

    setFiles((prev) => [...prev, ...validFiles]);
    setStatus({ type: null, message: '' }); // Clear any previous error
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: null, message: '' });

    const form = formRef.current;
    if (!form) return;

    // Create FormData object
    const formData = new FormData();
    
    // Append text fields
    formData.append('name', form.elements.name.value);
    formData.append('company', form.elements.company.value);
    formData.append('email', form.elements.email.value);
    formData.append('phone', form.elements.phone.value);
    formData.append('message', form.elements.message.value);
    formData.append('website_url', form.elements.website_url.value); // Honeypot

    // Append files
    files.forEach((file) => {
      formData.append('attachments', file);
    });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
        // Do NOT set Content-Type header, the browser will set it automatically with the boundary
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse! Jeg har modtaget din besked og vender tilbage til dig hurtigst muligt.'
        });
        form.reset();
        setFiles([]);
      } else {
        setStatus({
          type: 'error',
          message: result.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til mig direkte.'
        });
      }
    } catch (error) {
      console.error('Fejl ved afsendelse af formular:', error);
      setStatus({
        type: 'error',
        message: 'Der opstod en netværksfejl. Kontroller din internetforbindelse og prøv igen.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-zinc-50 min-h-screen">
      {/* Main Grid */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 uppercase mb-12">
            Kontakt <span className="text-zinc-900">Mig</span>
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Contact Info (5 cols) */}
            <div className="lg:col-span-5 space-y-8">
              {/* Contact Info Box */}
              <div className="bg-zinc-900 text-white rounded-2xl p-6 shadow-md border border-zinc-800 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold uppercase">Brug for hurtig hjælp?</h2>
                </div>
                <p className="text-sm font-medium leading-relaxed">
                  Står din maskine eller gaffeltruck stille, og har du brug for hurtig assistance? Ring til mig med det samme. Jeg rykker hurtigt ud for at minimere din nedetid!
                </p>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 py-3 text-sm font-bold text-white hover:bg-red-700 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  <span>Ring direkte: {companyInfo.phone}</span>
                </a>
              </div>

              {/* General Contact Details */}
              <div className="space-y-6 py-2">
                <h3 className="text-lg font-semibold text-zinc-900 border-b border-zinc-200 pb-3 uppercase">
                  Kontaktoplysninger
                </h3>
                <ul className="divide-y divide-zinc-200">
                  <li className="flex items-start gap-3.5 py-4 first:pt-0">
                    <MapPin className="h-5 w-5 text-zinc-900 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <span className="block font-semibold text-zinc-900">Adresse</span>
                      <span className="text-zinc-600">{companyInfo.address}</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3.5 py-4">
                    <Phone className="h-5 w-5 text-zinc-900 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <span className="block font-semibold text-zinc-900">Telefon</span>
                      <a href={`tel:${companyInfo.phone}`} className="text-zinc-600 hover:text-red-600 transition-colors">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3.5 py-4 last:pb-0">
                    <Mail className="h-5 w-5 text-zinc-900 shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <span className="block font-semibold text-zinc-900">E-mail</span>
                      <a href={`mailto:${companyInfo.email}`} className="text-zinc-600 hover:text-red-600 transition-colors break-all">
                        {companyInfo.email}
                      </a>
                    </div>
                  </li>
                </ul>
              </div>


            </div>

            {/* Right Column: Contact Form (7 cols) */}
            <div className="lg:col-span-7 bg-white rounded-2xl border border-zinc-200 p-6 md:p-8 shadow-sm space-y-6">
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-zinc-900 uppercase">Send mig en besked</h3>
                <p className="text-sm text-zinc-600">
                  Udfyld formularen nedenfor, så vender jeg tilbage med svar eller et uforpligtende tilbud inden for 24 timer på hverdage.
                </p>
              </div>

              {/* Status Messages */}
              {status.type === 'success' && (
                <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-800">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-sm font-medium">{status.message}</p>
                </div>
              )}

              {status.type === 'error' && (
                <div className="flex items-start gap-3 rounded-xl bg-rose-50 border border-rose-200 p-4 text-rose-800">
                  <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                  <p className="text-sm font-medium">{status.message}</p>
                </div>
              )}

              <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                <div className="sr-only absolute -top-96 -left-96" aria-hidden="true">
                  <label htmlFor="website_url">Do not fill this out if you are human</label>
                  <input
                    type="text"
                    name="website_url"
                    id="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="name" className="text-xs font-bold text-zinc-700 uppercase tracking-wider">
                      Navn <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      placeholder="Dit fulde navn"
                      className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm text-zinc-900 focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="company" className="text-xs font-bold text-zinc-700 uppercase tracking-wider">
                      Virksomhed
                    </label>
                    <input
                      type="text"
                      name="company"
                      id="company"
                      placeholder="Evt. firmanavn"
                      className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm text-zinc-900 focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="text-xs font-bold text-zinc-700 uppercase tracking-wider">
                      E-mail <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      placeholder="din@email.dk"
                      className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm text-zinc-900 focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="text-xs font-bold text-zinc-700 uppercase tracking-wider">
                      Telefonnummer <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      required
                      placeholder="Tlf. nr."
                      className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm text-zinc-900 focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="message" className="text-xs font-bold text-zinc-700 uppercase tracking-wider">
                    Besked <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    required
                    rows="5"
                    placeholder="Beskriv hvad du har brug for hjælp til (f.eks. maskintype, fejlbeskrivelse eller ønske om sikkerhedseftersyn)..."
                    className="w-full rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm text-zinc-900 focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none resize-none"
                  />
                </div>

                {/* File Upload Section */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-zinc-700 uppercase tracking-wider block">
                    Vedhæft billeder eller dokumenter (Valgfrit)
                  </label>
                  <div className="border-2 border-dashed border-zinc-300 rounded-xl p-4 text-center hover:border-red-500 transition-colors relative cursor-pointer bg-zinc-50">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <Upload className="h-6 w-6 text-zinc-400" />
                      <span className="text-sm font-semibold text-zinc-700">Klik for at vælge filer</span>
                      <span className="text-xs text-zinc-500">PDF, PNG, JPG, GIF eller WEBP (Maks. 5 filer, 10 MB pr. fil, 20 MB i alt)</span>
                    </div>
                  </div>

                  {/* List of uploaded files */}
                  {files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Valgte filer ({files.length}):</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {files.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-zinc-100 rounded-lg border border-zinc-200 text-xs">
                            <span className="truncate font-medium text-zinc-700 max-w-[180px]">{file.name}</span>
                            <span className="text-zinc-500 shrink-0">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="p-1 text-zinc-400 hover:text-rose-500 rounded-full hover:bg-zinc-200 transition-colors ml-2"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 py-3.5 text-sm font-bold text-white hover:bg-red-700 disabled:bg-zinc-200 disabled:text-zinc-400 disabled:cursor-not-allowed transition-all shadow-md shadow-red-600/10"
                >
                  {submitting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sender besked...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send besked</span>
                    </>
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
